/**
 * MapCredits Component
 *
 * Displays attribution for GeoJSON data sources.
 * Shows both the curator (who enriched/compiled the data) and original source.
 * Required for proper licensing compliance.
 */

import { Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { useState } from 'react';
import type { GeoJsonDatasetCredit } from '@/data/geojsonDatasets';

interface MapCreditsProps {
  credits: GeoJsonDatasetCredit[];
  className?: string;
  variant?: 'floating' | 'inline'; // floating for full-screen maps, inline for regular
}

export function MapCredits({ credits, className = '', variant = 'inline' }: MapCreditsProps) {
  const [isOpen, setIsOpen] = useState(false);

  if (credits.length === 0) return null;

  // Floating variant for full-screen maps
  if (variant === 'floating') {
    return (
      <div className={`fixed bottom-4 right-4 z-toast ${className}`}>
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleTrigger asChild>
            <Button
              variant="secondary"
              size="sm"
              className="bg-card/95 backdrop-blur-sm shadow-lg hover:bg-card/100"
            >
              <Info className="w-4 h-4 mr-2" />
              Credits
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2">
            <div className="bg-card/95 backdrop-blur-md shadow-xl rounded-lg p-4 max-w-sm border">
              <h4 className="font-semibold text-sm mb-2">Data Attribution</h4>
              <div className="space-y-3">
                {credits.map((credit, index) => (
                  <div key={index} className="text-xs space-y-1">
                    <div className="font-medium">{credit.name}</div>
                    <div className="text-muted-foreground">{credit.description}</div>
                    {credit.url && (
                      <a
                        href={credit.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline inline-flex items-center gap-1"
                      >
                        View source →
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>
    );
  }

  // Inline variant for regular map views
  return (
    <div className={`border-t bg-muted/30 p-3 ${className}`}>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Info className="w-3.5 h-3.5" />
            <span>Data Attribution</span>
          </div>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="h-7 text-xs">
              {isOpen ? 'Hide' : 'Show'}
            </Button>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent className="mt-3">
          <div className="space-y-2">
            {credits.map((credit, index) => (
              <div
                key={index}
                className="text-xs space-y-1 pb-2 border-b last:border-0 last:pb-0"
              >
                <div className="font-medium">{credit.name}</div>
                <div className="text-muted-foreground leading-relaxed">
                  {credit.description}
                </div>
                {credit.url && (
                  <a
                    href={credit.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline inline-flex items-center gap-1"
                  >
                    View source →
                  </a>
                )}
              </div>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
